import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, BookmarkSimple } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { SkeletonRows } from "@/components/Skeleton";

export default function BookmarksPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [list, setList] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!user || !user.role) { setFetching(false); return; }
    api.get("/bookmarks").then((r) => setList(r.data)).finally(() => setFetching(false));
  }, [user]);

  if (loading) return (
    <div data-testid="bookmarks-page-loading" className="px-6 md:px-10 lg:px-16 py-16">
      <SkeletonRows count={3} testid="bookmarks-page-skeleton" />
    </div>
  );

  if (!user || !user.role) {
    return (
      <div data-testid="bookmarks-page" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <h1 className="font-display font-bold text-4xl mb-3">{t("common.pleaseSignIn")}</h1>
        <p className="text-gray-600 mb-6">{t("bookmarksPage.signInBody")}</p>
        <Link to="/login" data-testid="bookmarks-login-link" className="btn-primary">{t("common.signIn")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="bookmarks-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="overline mb-3">{t("bookmarksPage.breadcrumb")}</div>
      <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tighter leading-none mb-2">{t("bookmarksPage.title")}</h1>
      <p className="text-gray-600">{t("bookmarksPage.intro")}</p>

      <div className="mt-10 border border-gray-200">
        {fetching && <div className="p-6"><SkeletonRows count={4} testid="bookmarks-skeleton" /></div>}
        {!fetching && list.length === 0 && (
          <EmptyState
            testid="bookmarks-empty"
            icon={<BookmarkSimple size={22} />}
            title={t("bookmarksPage.empty")}
            body={t("bookmarksPage.emptyBody")}
            action={{ label: t("bookmarksPage.browseLink"), to: "/conferences" }}
          />
        )}
        {list.map((c, i) => (
          <Link key={c.id} to={`/conferences/${c.id}`} data-testid={`bookmark-row-${i}`} className="block border-b border-gray-200 last:border-0 px-6 py-5 hover:bg-[#F9FAFB]">
            <div className="font-display font-semibold text-xl md:text-2xl">{c.title}</div>
            <div className="mt-1 text-sm text-gray-600 flex flex-wrap gap-4">
              <span className="flex items-center gap-1"><Calendar size={13} /> {format(new Date(c.start_date), "MMM d, yyyy")}</span>
              <span className="flex items-center gap-1"><MapPin size={13} /> {c.city}, {c.country}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
